export interface SuggestionCard {
  title: string;
  text: string;
}

// ── URL ───────────────────────────────────────────────────────────────────────

export const URL_SUGGESTION_CARDS: SuggestionCard[] = [
  { title: 'HTTPS Match', text: 'Create a regex to match any URL that starts with https://' },
  {
    title: 'Numeric ID',
    text: 'Create a regex to match a URL containing a numeric ID segment (e.g., /users/123)',
  },
  {
    title: 'Two Segments',
    text: 'Create a regex to match a URL path with exactly two segments (e.g., /api/resource)',
  },
  { title: 'File Extension', text: 'Create a regex to match URLs that end with .pdf' },
  { title: 'Version Prefix', text: 'Create a regex to match URLs that start with /v2/' },
  {
    title: 'UUID Segment',
    text: 'Create a regex to match URL paths like /users/550e8400-e29b-41d4-a716-446655440000',
  },
  {
    title: 'Trailing Slash',
    text: 'Create a regex to match the URL path /api/users with an optional trailing slash',
  },
  { title: 'API Path', text: 'Create a regex to match a URL path that starts with /api/' },
  {
    title: 'Dynamic Segment',
    text: 'Create a regex to match URL paths like /products/{id}/details where {id} is any value',
  },
  {
    title: 'Slug Path',
    text: 'Create a regex to match URL paths like /blog/my-first-post (lowercase letters, numbers, and hyphens)',
  },
  {
    title: 'Numeric End',
    text:
      'Create a regex to match URL paths ending with /orders/ followed by one or more digits (e.g. /orders/12345), where the numeric ID can be any positive integer.',
  },
  { title: 'Sub-Path Match', text: 'Create a regex to match any URL path that contains /api/v2/ as a sub-path' },
];

// ── Request Headers ───────────────────────────────────────────────────────────

export const HEADER_NAMED_POOLS: Record<string, SuggestionCard[]> = {
  authorization: [
    {
      title: 'Bearer Token',
      text: "Create a regex to match an Authorization header value like 'Bearer eyJhbGciOiJIUzI1NiJ9.abc.def'",
    },
    {
      title: 'Basic Auth',
      text: "Create a regex to match an Authorization header value like 'Basic dXNlcjpwYXNz'",
    },
    {
      title: 'API Key Format',
      text: 'Create a regex to match an Authorization header value that is exactly 32 hexadecimal characters',
    },
    {
      title: 'Bearer Prefix',
      text: "Create a regex to validate that an Authorization header starts with 'Bearer '",
    },
  ],
  'content-type': [
    { title: 'JSON Type', text: 'Create a regex to match a Content-Type header value of application/json' },
    { title: 'XML Type', text: 'Create a regex to match a Content-Type header value of application/xml' },
    {
      title: 'Multipart',
      text:
        "Create a regex to match Content-Type header values like 'multipart/form-data; boundary=----WebKitFormBoundary7MA4YWxkTrZu0gW'",
    },
    {
      title: 'With Charset',
      text:
        'Create a regex to match Content-Type headers with an optional charset parameter (e.g. application/json; charset=utf-8)',
    },
  ],
  accept: [
    { title: 'Accepts JSON', text: 'Create a regex to match an Accept header that contains application/json' },
    {
      title: 'Quality Factor',
      text:
        'Create a regex to match any Accept header media type with an optional quality factor parameter (e.g. application/json;q=0.9)',
    },
    { title: 'Wildcard Accept', text: 'Create a regex to match an Accept header value of */*' },
    {
      title: 'Multiple Types',
      text:
        'Create a regex to match a full Accept header value containing one or more comma-separated media types (e.g. application/json, text/html, */*)',
    },
  ],
  'user-agent': [
    {
      title: 'Chrome Agent',
      text:
        "Create a regex to match a Chrome User-Agent like 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/120.0.0.0 Safari/537.36'",
    },
    { title: 'Mobile Agent', text: "Create a regex to match any User-Agent string that contains 'Mobile'" },
    { title: 'Bot/Crawler', text: "Create a regex to match any User-Agent string that contains 'Googlebot'" },
    {
      title: 'Version Number',
      text: "Create a regex to match a User-Agent that contains a version like '/120.0.0.0'",
    },
  ],
  'x-request-id': [
    {
      title: 'UUID Format',
      text: "Create a regex to match an X-Request-ID like '550e8400-e29b-41d4-a716-446655440000' (UUID)",
    },
    {
      title: 'Fixed Length',
      text: 'Create a regex to match an X-Request-ID that is exactly 16 alphanumeric characters',
    },
    { title: 'Custom Prefix', text: "Create a regex to match an X-Request-ID that starts with 'req-'" },
  ],
  'x-correlation-id': [
    {
      title: 'UUID Format',
      text: "Create a regex to match an X-Correlation-ID like '550e8400-e29b-41d4-a716-446655440000' (UUID)",
    },
    {
      title: 'Alphanumeric',
      text: 'Create a regex to match an X-Correlation-ID that is exactly 24 alphanumeric characters',
    },
    { title: 'Service Prefix', text: "Create a regex to match an X-Correlation-ID that starts with 'svc-'" },
  ],
  'x-forwarded-for': [
    { title: 'Single IPv4', text: "Create a regex to match an X-Forwarded-For value like '192.168.1.1'" },
    {
      title: 'Multiple IPs',
      text: "Create a regex to match an X-Forwarded-For value like '192.168.1.1, 10.0.0.1, 172.16.0.1'",
    },
    {
      title: 'IPv6 Address',
      text: "Create a regex to match an X-Forwarded-For value like '2001:0db8:85a3:0000:0000:8a2e:0370:7334'",
    },
  ],
};

export const HEADER_GENERIC_POOL: SuggestionCard[] = [
  { title: 'Starts With', text: "Create a regex to match any header value that starts with 'sess_'" },
  {
    title: 'Contains UUID',
    text: "Create a regex to match a header value that contains a UUID like '550e8400-e29b-41d4-a716-446655440000'",
  },
  {
    title: 'ISO Date',
    text: "Create a regex to match a header value like '2024-03-15T10:30:00Z' (ISO 8601 timestamp)",
  },
  { title: 'Alphanumeric', text: 'Create a regex to match a header value containing only alphanumeric characters' },
  {
    title: 'Prefix + Suffix',
    text: "Create a regex to match a header value that starts with 'tok_' and ends with '_v2'",
  },
  {
    title: 'JWT Token',
    text: "Create a regex to match a header value like 'eyJhbGciOiJIUzI1NiJ9.eyJzdWIiOiIxMjMifQ.abc123' (JWT)",
  },
  { title: 'Positive Integer', text: 'Create a regex to match a header value that is a positive integer' },
  { title: 'Hex String', text: 'Create a regex to match a header value that is exactly 16 hexadecimal characters' },
  { title: 'Email Value', text: "Create a regex to match a header value like 'user@example.com'" },
  { title: 'CSV Values', text: "Create a regex to match a header value like 'red,green,blue'" },
  { title: 'Domain Name', text: "Create a regex to match a header value like 'api.example.com' (domain name)" },
  { title: 'Exact Match', text: "Create a regex to match a header value that is exactly 'production'" },
];

// ── Query Parameters ──────────────────────────────────────────────────────────

export const QUERY_PARAM_NAMED_POOLS: Record<string, SuggestionCard[]> = {
  page: [
    {
      title: 'Positive Integer',
      text: 'Create a regex to match a positive integer value.',
    },
    {
      title: 'Numeric Range',
      text: 'Create a regex to match an integer value between 1 and 100.',
    },
  ],
  limit: [
    {
      title: 'Positive Integer',
      text: 'Create a regex to match a positive integer value.',
    },
    {
      title: 'Max Cap',
      text: 'Create a regex to match a positive integer value with a maximum of 100.',
    },
    {
      title: 'Allowed Values',
      text: 'Create a regex to match a value that is exactly one of: 10, 25, 50, or 100.',
    },
  ],
  offset: [
    {
      title: 'Non-Negative',
      text: 'Create a regex to match a non-negative integer value (zero or greater).',
    },
    {
      title: 'Numeric Value',
      text: 'Create a regex to match a value containing only digits.',
    },
  ],
  sort: [
    {
      title: 'Field + Direction',
      text:
        'Create a regex to match the value of a sort parameter with field name and optional direction (e.g., name:asc)',
    },
    {
      title: 'Minus Prefix',
      text: "Create a regex to match a sort parameter value like '-date' (minus prefix for descending)",
    },
    {
      title: 'Allowed Fields',
      text: 'Create a regex to match a sort parameter value that is exactly one of: name, date, price',
    },
  ],
  order: [
    {
      title: 'Asc or Desc',
      text: "Create a regex to match the value of an order parameter that is exactly 'asc' or 'desc'",
    },
    {
      title: 'Case Insensitive',
      text: 'Create a regex to match the value of an order parameter case-insensitively',
    },
  ],
  filter: [
    {
      title: 'Key:Value',
      text: "Create a regex to match a filter parameter value like 'status:active' (key:value)",
    },
    {
      title: 'Multi Conditions',
      text:
        "Create a regex to match a filter parameter value like 'status:active,role:admin' (comma-separated key:value pairs)",
    },
    {
      title: 'With Operator',
      text: "Create a regex to match a filter parameter value like 'price:gt:100' (field:operator:value)",
    },
  ],
  search: [
    {
      title: 'Alphanumeric',
      text:
        'Create a regex to match the value of a search parameter containing only alphanumeric characters and spaces',
    },
    {
      title: 'Min Length',
      text: 'Create a regex to match the value of a search parameter with a minimum of 3 characters',
    },
    {
      title: 'Max Length',
      text: 'Create a regex to match the value of a search parameter of at most 50 characters',
    },
  ],
  q: [
    {
      title: 'Non-Empty',
      text: 'Create a regex to match the value of a q parameter that is a non-empty string',
    },
    {
      title: 'Alphanumeric',
      text: 'Create a regex to match the value of a q parameter containing alphanumeric characters',
    },
  ],
  status: [
    {
      title: 'Allowed Values',
      text: 'Create a regex to match the value of a status parameter that is one of: active, inactive, pending',
    },
    {
      title: 'Lowercase Only',
      text: 'Create a regex to match the value of a status parameter using lowercase letters only',
    },
  ],
  type: [
    {
      title: 'Lowercase Letters',
      text: 'Create a regex to match the value of a type parameter that contains only lowercase letters',
    },
    {
      title: 'Allowed Set',
      text: 'Create a regex to match a type parameter value that is exactly one of: user, admin, guest',
    },
  ],
  id: [
    {
      title: 'Positive Integer',
      text: 'Create a regex to match the value of an id parameter that is a positive integer',
    },
    { title: 'UUID', text: 'Create a regex to match the value of an id parameter that is a UUID' },
    {
      title: 'Alphanumeric',
      text: 'Create a regex to match the value of an id parameter containing only alphanumeric characters',
    },
  ],
};

export const QUERY_PARAM_GENERIC_POOL: SuggestionCard[] = [
  { title: 'Positive Integer', text: 'Create a regex to match a positive integer value.' },
  { title: 'UUID', text: 'Create a regex to match a UUID value (e.g., 550e8400-e29b-41d4-a716-446655440000).' },
  { title: 'Alphanumeric', text: 'Create a regex to match a value containing only alphanumeric characters.' },
  { title: 'Boolean', text: 'Create a regex to match a value that is exactly true or false.' },
  { title: 'Date Format', text: 'Create a regex to match a date value in YYYY-MM-DD format.' },
  { title: 'CSV List', text: 'Create a regex to match a comma-separated list of arbitrary values.' },
  { title: 'Non-Empty', text: 'Create a regex to match any non-empty string value.' },
  { title: 'Hex Value', text: 'Create a regex to match a value that is exactly 8 hexadecimal characters.' },
  { title: 'Fixed Set', text: 'Create a regex to match a value that is exactly one of: small, medium, large.' },
  { title: 'Email', text: 'Create a regex to match a valid email address value.' },
  {
    title: 'Numeric Range',
    text: 'Create a regex to match two integers separated by a hyphen, representing a range (e.g., 10-100).',
  },
  {
    title: 'URL Value',
    text: 'Create a regex to match a full URL value including protocol, domain, and optional path.',
  },
];

// ── Cookies ───────────────────────────────────────────────────────────────────

export const COOKIE_ALIASES: Record<string, string> = {
  session: 'session',
  sessionid: 'session',
  jsessionid: 'session',
  auth_token: 'auth_token',
  access_token: 'auth_token',
  token: 'auth_token',
  csrf_token: 'csrf_token',
  csrftoken: 'csrf_token',
  'xsrf-token': 'csrf_token',
  user_id: 'user_id',
  userid: 'user_id',
  remember_me: 'remember_me',
  logged_in: 'remember_me',
  locale: 'locale',
  lang: 'locale',
  theme: 'theme',
  dark_mode: 'theme',
  _ga: '_ga',
  _gid: '_ga',
};

export const COOKIE_NAMED_POOLS: Record<string, SuggestionCard[]> = {
  session: [
    {
      title: 'UUID Session',
      text: 'Create a regex to match a UUID value (e.g., 550e8400-e29b-41d4-a716-446655440000).',
    },
    {
      title: 'Alphanumeric',
      text: 'Create a regex to match a value that is exactly 32 alphanumeric characters.',
    },
    {
      title: 'JSESSIONID',
      text: 'Create a regex to match a value that is exactly 32 uppercase alphanumeric characters.',
    },
  ],
  auth_token: [
    {
      title: 'JWT Token',
      text:
        'Create a regex to match a JWT value in three base64url segments separated by dots (header.payload.signature).',
    },
    {
      title: 'Long Token',
      text: 'Create a regex to match a value that is exactly 64 alphanumeric characters.',
    },
    {
      title: 'Bearer Style',
      text:
        'Create a regex to match a Bearer token value in the format: Bearer followed by a space and a JWT (header.payload.signature).',
    },
  ],
  csrf_token: [
    {
      title: 'Hex CSRF',
      text: 'Create a regex to match a value that is exactly 32 hexadecimal characters.',
    },
    {
      title: 'Alphanumeric',
      text: 'Create a regex to match a value that is exactly 32 alphanumeric characters.',
    },
  ],
  user_id: [
    {
      title: 'Integer ID',
      text: 'Create a regex to match a positive integer value.',
    },
    {
      title: 'UUID',
      text: 'Create a regex to match a UUID value (e.g., 550e8400-e29b-41d4-a716-446655440000).',
    },
  ],
  remember_me: [
    {
      title: 'True or 1',
      text: "Create a regex to match a value that is exactly 'true' or '1'.",
    },
    {
      title: '0 or 1',
      text: "Create a regex to match a value that is exactly '0' or '1'.",
    },
  ],
  locale: [
    {
      title: 'Locale Code',
      text:
        'Create a regex to match a locale code in the format of two lowercase letters, a hyphen, and two uppercase letters (e.g., en-US).',
    },
    {
      title: 'Language Code',
      text: 'Create a regex to match a two-letter lowercase language code (e.g., en, fr, de).',
    },
  ],
  theme: [
    {
      title: 'Light or Dark',
      text: "Create a regex to match a value that is exactly 'light' or 'dark'.",
    },
    {
      title: 'Auto or System',
      text: "Create a regex to match a value that is exactly 'auto' or 'system'.",
    },
  ],
  _ga: [
    {
      title: 'GA Cookie',
      text:
        'Create a regex to match a Google Analytics _ga cookie format: GA followed by two numeric segments and two numeric timestamps, all separated by dots (e.g., GA1.2.123456789.1234567890).',
    },
    {
      title: 'GID Tracking',
      text:
        'Create a regex to match a Google Analytics _gid cookie format: GA followed by two numeric segments and two numeric timestamps, all separated by dots (e.g., GA1.2.987654321.1234567890).',
    },
  ],
};

export const COOKIE_GENERIC_POOL: SuggestionCard[] = [
  {
    title: 'UUID Value',
    text: 'Create a regex to match a UUID value (e.g., 550e8400-e29b-41d4-a716-446655440000).',
  },
  {
    title: 'JWT Token',
    text:
      'Create a regex to match a JWT value in three base64url segments separated by dots (header.payload.signature).',
  },
  {
    title: 'Alphanumeric',
    text: 'Create a regex to match a value containing only alphanumeric characters.',
  },
  {
    title: 'Positive Integer',
    text: 'Create a regex to match a positive integer value.',
  },
  {
    title: 'Boolean',
    text: "Create a regex to match a value that is exactly 'true' or 'false'.",
  },
  {
    title: 'Hex String',
    text: 'Create a regex to match a value that is exactly 32 hexadecimal characters.',
  },
  {
    title: 'Custom Prefix',
    text: "Create a regex to match a value that starts with 'sess_' followed by any characters.",
  },
  {
    title: 'Username Format',
    text: 'Create a regex to match a username value of 3 to 20 alphanumeric characters.',
  },
  {
    title: 'DateTime String',
    text: 'Create a regex to match an ISO 8601 datetime value in the format YYYY-MM-DDTHH:MM:SSZ.',
  },
  {
    title: 'CSV List',
    text: 'Create a regex to match a comma-separated list of arbitrary values.',
  },
  {
    title: 'Long Alphanumeric',
    text: 'Create a regex to match a value that is exactly 64 alphanumeric characters.',
  },
  {
    title: 'Email',
    text: 'Create a regex to match a valid email address value.',
  },
];

// ── Body Matchers ─────────────────────────────────────────────────────────────

export const BODY_REGEX_POOL: SuggestionCard[] = [
  {
    title: 'Non-Empty Body',
    text: 'Create a regex to match any request body that has at least one character.',
  },
  {
    title: 'Numeric ID',
    text: 'Create a regex to find any sequence of digits anywhere in a string.',
  },
  {
    title: 'Email Address',
    text: 'Create a regex to find a valid email address anywhere in a string.',
  },
  {
    title: 'UUID',
    text: 'Create a regex to find a UUID anywhere in a string (e.g., 550e8400-e29b-41d4-a716-446655440000).',
  },
  {
    title: 'Base64 String',
    text:
      'Create a regex to find a base64-encoded string anywhere in a string (only alphanumeric characters, +, /, and = padding).',
  },
  {
    title: 'JSON Body',
    text: "Create a regex to match a request body that starts with '{' (JSON object).",
  },
  {
    title: 'XML Body',
    text: "Create a regex to match a request body that starts with '<' (XML root element).",
  },
  {
    title: 'ISO Timestamp',
    text: 'Create a regex to find an ISO 8601 datetime anywhere in a string, in the format YYYY-MM-DDTHH:MM:SSZ.',
  },
  {
    title: 'Embedded URL',
    text: 'Create a regex to find a full URL anywhere in a string, including protocol, domain, and optional path.',
  },
  {
    title: 'Empty Body',
    text: 'Create a regex to match a string that contains only whitespace or is empty.',
  },
  {
    title: 'String Literal',
    text: "Create a regex to find the literal string 'ERROR' anywhere in a string.",
  },
  {
    title: 'Credit Card',
    text:
      'Create a regex to find a credit card number anywhere in a string, with digits optionally separated by hyphens or spaces in groups of 4.',
  },
];

export const BODY_JSONPATH_POOL: SuggestionCard[] = [
  {
    title: 'Root Field',
    text: 'Write a JSONPath expression to access the userId field at the root level (e.g., $.userId).',
  },
  {
    title: 'Nested Field',
    text: 'Write a JSONPath expression to access the email field inside user.profile (e.g., $.user.profile.email).',
  },
  {
    title: 'Array Wildcard',
    text: 'Write a JSONPath expression to match every element in the items array (e.g., $.items[*]).',
  },
  {
    title: 'First Element',
    text: 'Write a JSONPath expression to access the first element of the items array (e.g., $.items[0]).',
  },
  {
    title: 'Filter Condition',
    text: "Write a JSONPath expression to filter the users array, returning elements where status equals 'active'.",
  },
  {
    title: 'Field Exists',
    text: 'Write a JSONPath expression to find users in the users array where the email field exists.',
  },
  {
    title: 'Numeric Threshold',
    text: 'Write a JSONPath expression to filter the products array where price > 100.',
  },
  {
    title: 'Deep Nested',
    text: "Write a JSONPath expression to find any element with a field named 'id' anywhere in the document.",
  },
  {
    title: 'Boolean Field',
    text: 'Write a JSONPath expression to access the user.isActive boolean field.',
  },
  {
    title: 'Contains Substring',
    text: "Write a JSONPath expression to filter the users array where the email field ends with '@admin.com'.",
  },
  {
    title: 'Non-Empty Array',
    text: 'Write a JSONPath expression to access the items array when it has at least one element.',
  },
  {
    title: 'Status/Type Field',
    text: "Write a JSONPath expression to filter the orders array, returning elements where type equals 'premium'.",
  },
];

export const BODY_XPATH_POOL: SuggestionCard[] = [
  { title: 'Element by Name', text: 'Write an XPath expression to select all <user> elements.' },
  {
    title: 'Attribute Value',
    text: "Write an XPath expression to select <user> elements where the role attribute equals 'admin'.",
  },
  {
    title: 'Text Content',
    text: 'Write an XPath expression to get the text content of the <title> element.',
  },
  {
    title: 'Child Elements',
    text: 'Write an XPath expression to select all direct child elements of the <user> element.',
  },
  { title: 'Position', text: 'Write an XPath expression to select the third <item> element in a list.' },
  {
    title: 'Text Contains',
    text: "Write an XPath expression to find <description> elements whose text contains 'urgent'.",
  },
  {
    title: 'Attribute Exists',
    text: 'Write an XPath expression to select all <user> elements that have an email attribute.',
  },
  {
    title: 'Namespace',
    text: "Write an XPath expression to select <ns:user> elements where 'ns' is the namespace prefix.",
  },
  { title: 'Last Element', text: 'Write an XPath expression to select the last <item> element in a list.' },
  { title: 'Count Elements', text: 'Write an XPath expression to count all <user> elements.' },
  { title: 'Root Element', text: 'Write an XPath expression to select the root <users> element.' },
  {
    title: 'Numeric Attribute',
    text: 'Write an XPath expression to select <product> elements where the price attribute is greater than 100.',
  },
];

// ── Response Body ─────────────────────────────────────────────────────────────

export const RESPONSE_BODY_POOL: SuggestionCard[] = [
  {
    title: 'Echo Request Body',
    text: 'Create a Handlebars template that echoes the full request body back in the response.',
  },
  {
    title: 'Include Request URL',
    text: 'Create a Handlebars expression to include the request URL in the response body.',
  },
  {
    title: 'First Path Segment',
    text:
      'Create a Handlebars template that extracts the first path segment from the request URL and includes it in the response body.',
  },
  {
    title: 'Query Param Value',
    text:
      "Create a Handlebars template that returns a JSON response body with a single field 'userId' set to the value of the 'userId' query parameter.",
  },
  {
    title: 'Echo Header Value',
    text:
      "Create a Handlebars template that returns a JSON response body with a single field 'requestId' set to the X-Request-ID request header.",
  },
  {
    title: 'Method in JSON Response',
    text: 'Create a Handlebars template that includes the request HTTP method as a field in the JSON response body.',
  },
  {
    title: 'Conditional Response Field',
    text:
      "Create a Handlebars template with a JSON response body that includes a 'message' field with one value when the X-Role header equals 'admin', and a different value otherwise.",
  },
  {
    title: 'Mirror Request Fields',
    text:
      "Create a Handlebars template that returns a JSON response body with three fields: 'method' (request method), 'url' (request URL), and 'userAgent' (User-Agent header).",
  },
  {
    title: 'Echo Multiple Headers',
    text:
      "Create a Handlebars template that returns a JSON response body with three fields: 'requestId' (X-Request-ID), 'correlationId' (X-Correlation-ID), and 'userAgent' (User-Agent).",
  },
  {
    title: 'Personalize from Cookie',
    text:
      "Create a Handlebars template that reads the 'username' cookie from the request and includes it as a 'greeting' field in the JSON response body.",
  },
];

// ── Response Headers ──────────────────────────────────────────────────────────

export const RESPONSE_HEADER_POOL: SuggestionCard[] = [
  {
    title: 'Dynamic Content-Type',
    text: 'Create a Handlebars template to set a dynamic Content-Type header based on the request Accept header.',
  },
  {
    title: 'Echo X-Request-ID',
    text: 'Create a Handlebars expression that echoes the incoming X-Request-ID back in the response header.',
  },
  {
    title: 'Echo Request Method',
    text:
      'Create a Handlebars expression that includes the request HTTP method in a custom response header (e.g., X-Original-Method).',
  },
  {
    title: 'Location from Path',
    text: 'Create a Handlebars template to set a Location header using the request URL path.',
  },
  {
    title: 'Echo Authorization Header',
    text:
      'Create a Handlebars expression that includes the incoming Authorization header value in a custom forwarded response header.',
  },
  {
    title: 'Query Param in Header',
    text:
      "Create a Handlebars expression that sets an X-Trace-ID response header to the value of the 'traceId' request query parameter.",
  },
  {
    title: 'Cookie-Based Header',
    text:
      "Create a Handlebars expression that reads the 'sessionId' cookie from the request and includes its value in a custom X-Session-ID response header.",
  },
  {
    title: 'Cache-Control Conditional',
    text:
      "Create a Handlebars template that sets a Cache-Control response header to 'max-age=3600' when the request method is GET, otherwise 'no-cache'.",
  },
  {
    title: 'Dynamic WWW-Authenticate',
    text:
      "Create a Handlebars template that sets a WWW-Authenticate response header to 'Bearer realm=' followed by the request host (e.g., 'Bearer realm=api.example.com').",
  },
  {
    title: 'CORS Origin Echo',
    text:
      "Create a Handlebars template that sets the Access-Control-Allow-Origin response header to echo the incoming Origin header when present, or fall back to '*'.",
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

export function pickNRandom<T>(arr: T[], n: number): T[] {
  return [...arr].sort(() => Math.random() - 0.5).slice(0, n);
}

function buildCardSelection(namedPool: SuggestionCard[] | undefined, genericPool: SuggestionCard[]): SuggestionCard[] {
  if (!namedPool || namedPool.length === 0) {
    return pickNRandom(genericPool, 4);
  }
  if (namedPool.length >= 4) {
    return pickNRandom(namedPool, 4);
  }
  const backfill = pickNRandom(genericPool, 4 - namedPool.length);
  return [...namedPool, ...backfill];
}

export function resolveBodyCards(matcherType: string | undefined): SuggestionCard[] {
  switch (matcherType) {
    case 'matches':
    case 'not_matches':
      return pickNRandom(BODY_REGEX_POOL, 4);
    case 'matches_json':
      return pickNRandom(BODY_JSONPATH_POOL, 4);
    case 'matches_xml':
    case 'matches_xml_cdata':
      return pickNRandom(BODY_XPATH_POOL, 4);
    default:
      return [];
  }
}

export function resolveFieldCards(callerId: string, fieldName: string | undefined): SuggestionCard[] | null {
  const normalized = (fieldName || '').trim().toLowerCase();

  if (callerId.includes('matchers-header')) {
    return buildCardSelection(HEADER_NAMED_POOLS[normalized], HEADER_GENERIC_POOL);
  }
  if (callerId.includes('matchers-query-params')) {
    return buildCardSelection(QUERY_PARAM_NAMED_POOLS[normalized], QUERY_PARAM_GENERIC_POOL);
  }
  if (callerId.includes('matchers-cookies')) {
    const canonicalKey = COOKIE_ALIASES[normalized];
    return buildCardSelection(canonicalKey ? COOKIE_NAMED_POOLS[canonicalKey] : undefined, COOKIE_GENERIC_POOL);
  }
  return null;
}
